import { FC, useCallback, useEffect, useState } from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Modal as AntModal, Tag } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { getWebHookContainerRoute } from 'routes/events/list';
import { Button, Dropdown, Icon, Table, Typography, message } from 'ui';
import BackLink from 'ui/BackLink';

import { useLazyGetEventDetailQuery } from 'services/events/eventsApiService';
import {
  useDeleteWebHookMutation,
  useLazyGetWebHooksQuery,
  useUpdateWebHookMutation,
} from 'services/promoter/promoterApiService';
import { setActiveEvent } from 'slices/eventsSlice';
import { useAppDispatch } from 'store/index';

import EventsLoadingState from 'containers/events-loading-state';

import { EventCreatedType } from 'types/events/eventTypes';
import { WebHook } from 'types/promoters/promoterTypes';

import s from './WebHook.module.scss';

const { confirm } = AntModal;

const ACTIVE_DROPDOWN_ITEMS = [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'delete', label: <div className={s.dropdownItem}>Delete</div> },
  { key: 'active', label: <div className={s.dropdownItem}>Enable</div> },
];

const DEACTIVE_DROPDOWN_ITEMS = [
  { key: 'edit', label: <div className={s.dropdownItem}>Edit</div> },
  { key: 'delete', label: <div className={s.dropdownItem}>Delete</div> },
  { key: 'active', label: <div className={s.dropdownItem}>Disable</div> },
];

const WebHookComponent: FC = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [getWebHooks, { isFetching, data: webHooks }] = useLazyGetWebHooksQuery();
  const [updateWebHook, { isLoading: updating }] = useUpdateWebHookMutation();
  const [deleteWebHook, { isLoading: deleting }] = useDeleteWebHookMutation();
  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();
  const [openedDropdown, setOpenedDropdown] = useState<boolean>(false);
  const [showHooks, setShowHooks] = useState<WebHook[]>([]);

  const handleGetEditingEvent = useCallback(
    async (eventId: string) => {
      const res = await getEventDetail(eventId);

      if ('data' in res) {
        dispatch(setActiveEvent(res.data as EventCreatedType));
      }
    },
    [dispatch, getEventDetail],
  );
  useEffect(() => {
    if (eventId) {
      handleGetEditingEvent(eventId);
    }
  }, [eventId, handleGetEditingEvent]);
  useEffect(() => {
    if (webHooks?.webhooks) {
      setShowHooks(webHooks?.webhooks);
    }
  }, [webHooks, setShowHooks]);

  useEffect(() => {
    if (eventId) {
      getWebHooks({ eventId: eventId });
    }
  }, [eventId, getWebHooks]);

  const goToWebHookContainer = useCallback(() => {
    navigate(getWebHookContainerRoute(String(eventId || ''), 'create'));
  }, [eventId, navigate]);

  const showDeleteConfirm = async (id: string) => {
    confirm({
      title: 'Would you like to delete this web hook?',
      icon: <InfoCircleOutlined />,
      content: '',
      centered: true,
      mask: true,
      async onOk() {
        if (id) {
          const res = await deleteWebHook(id);

          if ('data' in res) {
            message.success('Web hook deleted successfully.');
            setShowHooks(res.data.webhooks);
          }
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleClickDropdownItems = async (event: { key: string }, record) => {
    setOpenedDropdown(false);
    if (event.key === 'delete') {
      showDeleteConfirm(record?.id);
    }

    if (event.key === 'edit') {
      setOpenedDropdown(false);
      navigate(getWebHookContainerRoute(String(eventId || ''), String(record?.id || '')));
    }

    if (event.key === 'active') {
      setOpenedDropdown(false);

      const transformed = record?.ticketTypes.map(({ id }) => ({
        ticketTypeId: id,
      }));

      const result = {
        ...record,
        active: record.active ? false : true,
        eventId: eventId,
        ticketTypes: transformed,
      };

      const res = await updateWebHook({ id: record?.id, data: result });

      if ('data' in res) {
        setShowHooks(res.data.webhooks);
        if (record.active) {
          message.success('Web hook has been disabled.');
        } else {
          message.success('Web hook has been enabled.');
        }
      }
    }
  };

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 140,
    },
    {
      title: 'Web Hook Active',
      dataIndex: 'active',
      key: 'active',
      width: 120,
      render: (active: boolean) => {
        return <Tag color={active ? 'cyan' : 'error'}>{active ? 'True' : 'False'}</Tag>;
      },
    },
    {
      title: 'Type',
      dataIndex: 'webHookType',
      key: 'webHookType',
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Dropdown
          className="no-row-click"
          items={record?.active ? DEACTIVE_DROPDOWN_ITEMS : ACTIVE_DROPDOWN_ITEMS}
          placement="bottomRight"
          handleItemClick={(event) => handleClickDropdownItems(event, record)}
          open={openedDropdown === record.id}
          onOpenChange={(open) => setOpenedDropdown(open ? record.id : null)}>
          <div className={s.dropdownButton}>
            <Icon name="verticalDots" color="#7d7da2" />
          </div>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.header}>
        <div className={s.titileHeader}>
          {eventDetail && (
            <Typography type="h2" weight={700}>
              &quot;{eventDetail?.basicInfo?.eventName ?? ''}&quot;&nbsp;Web hooks
            </Typography>
          )}
        </div>

        {/*<Breadcrumbs routes={BREADCRUMBS_ROUTES} className={s.breadcrumbs} />*/}

        <div className={s.searchBlock}>
          <div>
            <Button size="large" onClick={goToWebHookContainer} className={s.createBtn}>
              +&nbsp; CREATE NEW
            </Button>
          </div>
        </div>
      </div>

      <div style={{ overflowY: 'auto', marginTop: 16 }}>
        {webHooks?.webhooks ? (
          <Table
            loading={deleting || updating || isFetching}
            rowKey="id"
            dataSource={showHooks || []}
            columns={columns}
          />
        ) : isFetching ? (
          <div className={s.emptyWrapper}>
            <EventsLoadingState size={240} />
          </div>
        ) : (
          <div className={s.emptyWrapper}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: 12,
              }}>
              <Typography color="grey">You have no web hook for this event yet</Typography>
              <Button onClick={goToWebHookContainer} size="large" className={s.createBtn}>
                +&nbsp; CREATE NEW
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebHookComponent;
